// Per-site audit report sync. Each site's report is the set of AUDIT_KEYS below, stored
// server-side as an opaque localStorage-key → value blob (keyed by normalized host).
// The client mirrors the ACTIVE site's blob into localStorage so the existing synchronous
// readers in App.tsx keep working; switching sites swaps localStorage + remounts App.

// Every localStorage key that belongs to a single site's audit report.
export const AUDIT_KEYS = [
  "full_audit_sections", "full_audit_analyses", "seo_audit_ever_run",
  "cover_business_name", "cover_website_url", "cover_full_address", "cover_location",
  "cover_kw_1", "cover_kw_2", "cover_kw_3", "cover_kw_4", "cover_kw_5",
  "seo_target_domain", "seo_competitor_domains", "seo_full_metrics",
  "seo_keyword_positions", "seo_keyword_volumes", "seo_map_pack", "seo_brand_names", "seo_cover_strip",
  "seo_last_tab_crawl", "cover_last_audit_date", "seo_tab_crawl_dates",
  "competitors_last_crawl", "competitors_brand_data",
  "seo_v5_state", // fix approvals (queued/done/ignored) — per site
  // Per-site Global Custom Instructions (prefilled into every New Page's Custom Instructions
  // box when enabled). Config, NOT crawl data — kept out of TAB_DATA_KEYS so a tab-data reset
  // never wipes them.
  "seo_global_instructions", "seo_global_instructions_enabled",
  // Per-site optional DEFAULTS that prefill the matching New Page Details fields (and the
  // Service Area tab). Config, per-site.
  "seo_default_post_category", "seo_default_post_tags", "seo_default_page_tags",
  "seo_default_sa_parent", "seo_default_sa_tags", "seo_default_tone",
  // Sitemap tab — the crawled per-page table (JSON rows) + last-crawl timestamp.
  "seo_sitemap_rows", "seo_sitemap_crawled_at",
  // Power-Tools tabs — each renders a dedicated component that persists its pasted URLs +
  // generated cards in localStorage. These are per-site working data, so they must swap with
  // the active site (otherwise a previous site's cards linger after switching). Kept in the
  // blob so switching AWAY preserves them and switching BACK restores them ("clear, not delete").
  "lsg_cd_titles", "lsg_cd_cards", "lsg_cd_snapshot", "lsg_cd_seq",   // Content Drawer
  "lsg_bps_urls", "lsg_bps_cards",                                     // Express Schema (Bulk Page Schema)
  "lsg_bmd_urls", "lsg_bmd_cards",                                     // Express Meta Data
  "lsg_bil_urls", "lsg_bil_cards",                                     // Express Internal Links
  "lsg_lhk_branded", "lsg_lhk_pages", "lsg_lhk_generated",             // Low-Hanging KW Insertion
  "seo_competitor_mode",                                              // cover Custom Competitors toggle (per-site)
  // Service Area Buildout — inputs (address/radius/cities/business/category/services/parent/
  // center) + generated cards. Per-site so they swap with the active site.
  "lsg_sa_address", "lsg_sa_radius", "lsg_sa_cities", "lsg_sa_business", "lsg_sa_category",
  "lsg_sa_svc_list", "lsg_sa_addr_hidden", "lsg_sa_drive", "lsg_sa_no_map", "lsg_sa_parent", "lsg_sa_tags", "lsg_sa_center",
  "lsg_sa_custom_title", "lsg_sa_title_fmt",
  "lsg_sa_ci", "lsg_sa_ci_overwrite", "lsg_sa_cards", "lsg_sa_snapshot", "lsg_sa_seq",
  // Conversational SEO — chat transcript, per-site.
  "seo_chat_history",
  // Topical Coverage Engine — the 5 competitor-domain inputs + last Page Inventory table HTML, plus
  // the location / primary keyword / additional keywords and the last People Also Ask results.
  "lsg_tce_competitors", "lsg_tce_result",
  "lsg_tce_location", "lsg_tce_primary_kw", "lsg_tce_extra_kws", "lsg_tce_paa", "lsg_tce_ac",
  // …and its Competitor Pages You Don't Have list, page-title cache, and Create Page cards.
  "lsg_tce_gaps", "lsg_tce_titles", "lsg_tce_cards", "lsg_tce_snapshot", "lsg_tce_seq", "lsg_tce_created",
];

// localStorage keys for the per-site Global Custom Instructions (also members of AUDIT_KEYS).
export const GI_TEXT_KEY = "seo_global_instructions";
export const GI_ENABLED_KEY = "seo_global_instructions_enabled";

// Per-site optional defaults (all members of AUDIT_KEYS). Each prefills the matching field when
// the user hasn't already set one on the page/card.
export const SITE_DEFAULT_KEYS = {
  postCategory: "seo_default_post_category",
  postTags:     "seo_default_post_tags",
  pageTags:     "seo_default_page_tags",
  saParent:     "seo_default_sa_parent",
  saTags:       "seo_default_sa_tags",
  tone:         "seo_default_tone",
} as const;
export interface SiteDefaults { postCategory: string; postTags: string; pageTags: string; saParent: string; saTags: string; tone: string; }
const EMPTY_DEFAULTS: SiteDefaults = { postCategory: "", postTags: "", pageTags: "", saParent: "", saTags: "", tone: "" };

const ACTIVE_KEY = "lsg_active_site";

export interface AuditSummary { siteKey: string; label: string; updatedAt: string; }

// Normalized host used as the per-site key: drop protocol/path, strip leading www, lowercase.
export function siteKey(url: string): string {
  const raw = (url || "").trim();
  if (!raw) return "";
  try {
    const host = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).host;
    return host.replace(/^www\./i, "").toLowerCase();
  } catch {
    return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0].toLowerCase();
  }
}

// ── Active-site bookkeeping (mirrored in localStorage) ────────
export function getActiveSiteKey(): string { try { return localStorage.getItem(ACTIVE_KEY) ?? ""; } catch { return ""; } }
export function setActiveSiteKeyLocal(key: string): void { try { localStorage.setItem(ACTIVE_KEY, key); } catch { /* ignore */ } }

// ── Blob <-> localStorage ─────────────────────────────────────
export function collectBlob(): Record<string, string> {
  const data: Record<string, string> = {};
  for (const k of AUDIT_KEYS) {
    const v = localStorage.getItem(k);
    if (v !== null) data[k] = v;
  }
  return data;
}

// Write a blob's keys into localStorage; clear any AUDIT_KEY not present (clean swap).
export function applyBlob(data: Record<string, string>): void {
  for (const k of AUDIT_KEYS) {
    const v = data[k];
    try {
      if (v === undefined) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch { /* ignore */ }
  }
}

export function clearAuditLocal(): void {
  for (const k of AUDIT_KEYS) { try { localStorage.removeItem(k); } catch { /* ignore */ } }
  // Don't leave a pointer to one user's running audit job for the next session.
  try { localStorage.removeItem("lsg_active_audit_job"); } catch { /* ignore */ }
  // Clear unpublished New-Page content drafts so they don't leak to the next user/site.
  try { localStorage.removeItem("lsg_create_drafts"); } catch { /* ignore */ }
}

// True if the browser already holds a report worth migrating on first login.
function hasLocalReport(): boolean {
  return !!(localStorage.getItem("full_audit_sections") || localStorage.getItem("cover_website_url") || localStorage.getItem("cover_business_name"));
}

function deriveLabel(): string {
  const name = (localStorage.getItem("cover_business_name") ?? "").trim();
  if (name) return name;
  const key = siteKey(localStorage.getItem("cover_website_url") ?? "");
  return key || "My Site";
}

// ── Server I/O ────────────────────────────────────────────────
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok) throw new Error((data.error as string) || `Request failed (${res.status})`);
  return data as T;
}

export async function fetchAuditList(): Promise<{ sites: AuditSummary[]; activeSiteKey?: string }> {
  return api<{ sites: AuditSummary[]; activeSiteKey?: string }>("/api/audit/list");
}

export async function fetchAudit(key: string): Promise<{ label: string; data: Record<string, string> } | null> {
  const { blob } = await api<{ blob: { label: string; data: Record<string, string> } | null }>(`/api/audit/${encodeURIComponent(key)}`);
  return blob;
}

async function putAudit(key: string, data: Record<string, string>, label?: string): Promise<void> {
  await api(`/api/audit/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ label, data }) });
}

// Overwrite a site's full data blob server-side — used by the backup importer to restore an
// exported site (tab data + custom instructions) under its key.
export async function importAuditBlob(key: string, data: Record<string, string>, label?: string): Promise<void> {
  await putAudit(key, data, label);
}

export async function setActiveSiteServer(key: string): Promise<void> {
  await api("/api/audit/active", { method: "PUT", body: JSON.stringify({ siteKey: key }) });
}

export async function removeAuditServer(key: string): Promise<void> {
  await api(`/api/audit/${encodeURIComponent(key)}`, { method: "DELETE" });
}

// ── Per-site Global Custom Instructions ───────────────────────
// Read a site's global instructions. The ACTIVE site's values live in localStorage (they're
// AUDIT_KEYS, swapped in on switch); a non-active site is fetched from its server blob.
export async function loadSiteInstructions(key: string): Promise<{ text: string; enabled: boolean; defaults: SiteDefaults }> {
  const readDefaults = (get: (k: string) => string): SiteDefaults => ({
    postCategory: get(SITE_DEFAULT_KEYS.postCategory),
    postTags:     get(SITE_DEFAULT_KEYS.postTags),
    pageTags:     get(SITE_DEFAULT_KEYS.pageTags),
    saParent:     get(SITE_DEFAULT_KEYS.saParent),
    saTags:       get(SITE_DEFAULT_KEYS.saTags),
    tone:         get(SITE_DEFAULT_KEYS.tone),
  });
  if (key && key === getActiveSiteKey()) {
    const g = (k: string) => { try { return localStorage.getItem(k) ?? ""; } catch { return ""; } };
    return { text: g(GI_TEXT_KEY), enabled: g(GI_ENABLED_KEY) === "true", defaults: readDefaults(g) };
  }
  const blob = await fetchAudit(key).catch(() => null);
  const gd = (k: string) => blob?.data[k] ?? "";
  return { text: gd(GI_TEXT_KEY), enabled: gd(GI_ENABLED_KEY) === "true", defaults: readDefaults(gd) };
}

// Save a site's global instructions (+ optional per-site defaults). Active site → localStorage +
// debounced flush (the flush writes the whole blob). Non-active site → read-modify-write its
// server blob directly. `defaults` is optional so existing callers (e.g. the backup importer)
// keep working unchanged.
export async function saveSiteInstructions(key: string, text: string, enabled: boolean, label?: string, defaults?: SiteDefaults): Promise<void> {
  if (!key) return;
  const d = defaults ?? EMPTY_DEFAULTS;
  const writeDefaults = (set: (k: string, v: string) => void) => {
    if (!defaults) return; // don't clobber stored defaults when the caller didn't supply them
    (Object.keys(SITE_DEFAULT_KEYS) as Array<keyof SiteDefaults>).forEach(f => set(SITE_DEFAULT_KEYS[f], (d[f] ?? "").trim()));
  };
  if (key === getActiveSiteKey()) {
    try {
      localStorage.setItem(GI_TEXT_KEY, text);
      localStorage.setItem(GI_ENABLED_KEY, enabled ? "true" : "false");
      writeDefaults((k, v) => localStorage.setItem(k, v));
    } catch { /* ignore */ }
    scheduleFlush();
    return;
  }
  const blob = await fetchAudit(key).catch(() => null);
  const data: Record<string, string> = { ...(blob?.data ?? {}) };
  data[GI_TEXT_KEY] = text;
  data[GI_ENABLED_KEY] = enabled ? "true" : "false";
  writeDefaults((k, v) => { data[k] = v; });
  await putAudit(key, data, blob?.label ?? label);
}

// ── Background-job flush guard ─────────────────────────────────
// While a durable crawl job is writing a site's blob server-side (checkpointing each
// finished section), the client must NOT flush its in-progress localStorage blob — a
// full-overwrite PUT would clobber the server's checkpoints. The job owns writes until
// it finishes; the client re-hydrates afterward (or on next login).
// Ref-counted: several tabs can crawl one site at once, so the guard only lifts when the
// LAST active job for that site finishes.
const activeJobCounts = new Map<string, number>();
export function setSiteJobActive(key: string, active: boolean): void {
  if (!key) return;
  const n = activeJobCounts.get(key) ?? 0;
  if (active) activeJobCounts.set(key, n + 1);
  else if (n <= 1) activeJobCounts.delete(key);
  else activeJobCounts.set(key, n - 1);
}
export function isSiteJobActive(key: string): boolean { return (activeJobCounts.get(key) ?? 0) > 0; }

// ── Debounced flush of the active site's blob ─────────────────
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// True only during switchSite's swap window. While set, NO flush may run — otherwise a flush
// could persist the leaving site's localStorage under the arriving site's key (or vice versa)
// and cross-contaminate the two blobs.
let switchInProgress = false;

export function scheduleFlush(label?: string): void {
  if (switchInProgress) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => { void flushNow(label); }, 1200);
}

export async function flushNow(label?: string): Promise<void> {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (switchInProgress) return;
  const key = getActiveSiteKey();
  if (!key) return;
  if (isSiteJobActive(key)) return; // a job owns this site's blob right now
  try { await putAudit(key, collectBlob(), label ?? deriveLabel()); } catch { /* offline — keep local copy */ }
}

// ── Hydration on login ────────────────────────────────────────
// Loads the active site's report into localStorage (so App reads it synchronously on mount).
// First login with a browser-only report migrates it into a bucket. Returns the resolved
// active key + the site list for the switcher.
export async function loadActiveAudit(): Promise<{ activeSiteKey: string; sites: AuditSummary[] }> {
  let list: { sites: AuditSummary[]; activeSiteKey?: string };
  try { list = await fetchAuditList(); }
  catch { return { activeSiteKey: getActiveSiteKey(), sites: [] }; } // not authed / offline

  // Migration: server has no buckets yet but this browser holds a report → upload it.
  if (list.sites.length === 0) {
    if (hasLocalReport()) {
      const key = siteKey(localStorage.getItem("cover_website_url") ?? "") || "default";
      const label = deriveLabel();
      try {
        await putAudit(key, collectBlob(), label);
        await setActiveSiteServer(key);
      } catch { /* ignore — keep local */ }
      setActiveSiteKeyLocal(key);
      return { activeSiteKey: key, sites: [{ siteKey: key, label, updatedAt: new Date(0).toISOString() }] };
    }
    setActiveSiteKeyLocal("");
    clearAuditLocal();
    return { activeSiteKey: "", sites: [] };
  }

  // Resolve the active bucket (fall back to the first if the server never set one).
  const active = (list.activeSiteKey && list.sites.some(s => s.siteKey === list.activeSiteKey))
    ? list.activeSiteKey
    : list.sites[0].siteKey;
  try {
    const blob = await fetchAudit(active);
    if (blob) applyBlob(blob.data); else clearAuditLocal();
  } catch { /* keep whatever is local */ }
  setActiveSiteKeyLocal(active);
  if (active !== list.activeSiteKey) { try { await setActiveSiteServer(active); } catch { /* ignore */ } }
  return { activeSiteKey: active, sites: list.sites };
}

// Switch the active site: persist current → set new active → load its blob (or start a
// blank report seeded with the site URL). Caller remounts App afterward.
export async function switchSite(key: string, opts?: { siteUrl?: string; label?: string }): Promise<void> {
  await flushNow();                       // save the site we're leaving (active key still = old)
  // Fetch the arriving site's blob BEFORE flipping the active key, so there's never a window where
  // getActiveSiteKey() is the new site but localStorage still holds the old site's data — a flush
  // in that window would write the old data under the new key and cross-contaminate the blobs.
  let blob: { label: string; data: Record<string, string> } | null = null;
  try { blob = await fetchAudit(key); } catch { /* ignore */ }
  switchInProgress = true;                // block any flush during the swap below
  try {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; } // drop any pending flush
    setActiveSiteKeyLocal(key);
    if (blob) {
      applyBlob(blob.data);
    } else {
      // New site with no report yet — start blank but seed the audited URL/name so the next
      // audit keys under this site.
      clearAuditLocal();
      if (opts?.siteUrl) { try { localStorage.setItem("cover_website_url", opts.siteUrl); } catch { /* ignore */ } }
      if (opts?.label) { try { localStorage.setItem("cover_business_name", opts.label); } catch { /* ignore */ } }
    }
  } finally {
    switchInProgress = false;
  }
  void setActiveSiteServer(key).catch(() => {}); // persist the new active pointer (non-blocking)
}
